import { useCallback, useRef, useState } from "react";

// Colțurile grupurilor lipite (ListUI, InputUI.Group, grila din ChoiceUI): fiecare
// element își rotunjește singur colțurile care stau la marginea grupului — clasele
// is-tl / is-tr / is-bl / is-br — în loc ca grupul să le taie cu overflow: hidden.
// Așa inelul de focus, umbra sau conturul unui element nu mai sunt tăiate, iar
// elementul arată la fel și scos din grup.

/**
 * Câte coloane are grila de fapt, măsurat pe pagină: urmează media query-urile
 * (3 coloane → 1 pe telefon) și auto-fit. Întoarce [coloane, ref pentru grilă].
 */
export function useGridColumns(fallback = 1) {
   const [cols, setCols] = useState(fallback);
   const observer = useRef(null);

   const ref = useCallback((el) => {
      if (observer.current) {
         observer.current.disconnect();
         observer.current = null;
      }
      if (!el || typeof window === "undefined") return;
      const measure = () => {
         // ascunsă (display: none, „Mai multe” închis) grila n-are coloane de fapt:
         // CSS-ul dă textul „repeat(2, …)”, care s-ar număra greșit; rămâne ce era
         if (!el.getClientRects().length) return;
         const tracks = window.getComputedStyle(el).gridTemplateColumns;
         if (!tracks || tracks === "none") return;
         // auto-fit lasă coloanele goale cu 0px: nu se numără
         const n = tracks.trim().split(/\s+/).filter((t) => /px$/.test(t) && parseFloat(t) > 0).length;
         if (n > 0) setCols((c) => (c === n ? c : n));
      };
      measure();
      if (typeof ResizeObserver !== "undefined") {
         observer.current = new ResizeObserver(measure);
         observer.current.observe(el);
      }
   }, []);

   return [cols, ref];
}

/** Pune mai multe ref-uri pe același element (ref-ul primit din afară + al grilei). */
export function mergeRefs(...refs) {
   return (el) => {
      refs.forEach((r) => {
         if (typeof r === "function") r(el);
         else if (r) r.current = el;
      });
   };
}

const ALL = { tl: true, tr: true, bl: true, br: true };

/**
 * Colțurile de la margine pentru fiecare element dintr-o grilă cu `cols` coloane.
 * spans: pentru fiecare element, câte coloane ia (1, n sau "full" = tot rândul).
 * edges: colțurile grupului însuși (un rând dintr-un grup mai mare le primește de la
 *        părinte; implicit toate) — un colț e rotunjit doar dacă e și al grupului.
 * Un colț e la margine când nu are vecin pe niciuna din cele două laturi ale lui
 * (sus / jos și stânga / dreapta); colțurile dintr-o „treaptă” (ultimul rând mai
 * scurt) rămân drepte, ca într-o formă lipită.
 * Întoarce, pentru fiecare element, { tl, tr, bl, br }.
 */
export function joinedCorners(spans, cols, edges = ALL) {
   const n = Math.max(1, cols || 1);
   const rows = [];
   const place = [];
   let row = 0;
   let col = 0;
   spans.forEach((s, i) => {
      const span = s === "full" ? n : Math.min(n, Math.max(1, Number(s) || 1));
      if (col + span > n) {
         row += 1;
         col = 0;
      }
      place.push({ row, c0: col, c1: col + span - 1 });
      rows[row] = rows[row] || [];
      for (let c = col; c < col + span; c += 1) rows[row][c] = i;
      col += span;
      if (col >= n) {
         row += 1;
         col = 0;
      }
   });
   const has = (r, c) => c >= 0 && c < n && rows[r] != null && rows[r][c] != null;
   return place.map(({ row: r, c0, c1 }) => ({
      tl: edges.tl && !has(r - 1, c0) && !has(r, c0 - 1),
      tr: edges.tr && !has(r - 1, c1) && !has(r, c1 + 1),
      bl: edges.bl && !has(r + 1, c0) && !has(r, c0 - 1),
      br: edges.br && !has(r + 1, c1) && !has(r, c1 + 1),
   }));
}

/** Clasele colțurilor: { tl, tr, bl, br } → "is-tl is-br". */
export function cornerClass(corners) {
   if (!corners) return "";
   return ["tl", "tr", "bl", "br"]
      .filter((k) => corners[k])
      .map((k) => `is-${k}`)
      .join(" ");
}
